import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { Button, Alert } from 'react-bootstrap';
import { VscLoading } from 'react-icons/vsc';
import { fetchWrapper } from '@/_helpers';
import { toast } from 'react-toastify';

const FormContainer = styled.div`
    width: 100%;
    padding: 2rem;
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const FormTitle = styled.h2`
    margin-bottom: 2rem;
    color: #333;
`;

const FormGroup = styled.div`
    margin-bottom: 1.5rem;
`;

const Label = styled.label`
    display: block;
    margin-bottom: 0.5rem;
`;

const Input = styled.input`
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 4px;

    &:focus {
        outline: none;
        border-color: #646cff;
    }
`;

const Select = styled.select`
    width: 100%;
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background-color: white;

    &:focus {
        outline: none;
        border-color: #646cff;
    }
`;

const LoadingMessage = styled.div`
    text-align: center;
    padding: 2rem;
`;
const BLOCK_IDENTIFIER = 'client_basic_information';
const BasicInformation = ({ clientId, teamId }) => {
    const [data, setData] = useState({
        dob: '',
        gender: '',
        diagnosisCode: '',
    });
    const [isBusy, setIsBusy] = useState(true);
    const [error, setError] = useState(null);
    const [responseData, setResponseData] = useState(null);
    const fetch = async () => {
        if (!clientId) {
            setError('Failed to load data. Please try again later.');
        }
        try {
            const response = await fetchWrapper.get(
                `evv-datablock/client/${clientId}/${BLOCK_IDENTIFIER}${teamId ? `?team=${teamId}` : ''}`
            );
            setResponseData(response);
            const _data = response?.data || {};
            setData({
                dob: _data.dob || '',
                gender: _data.gender || '',
                diagnosisCode: _data.diagnosisCode || '',
            });
            setIsBusy(false);
        } catch (err) {
            if (err && err?.code === 404) {
                // skip error message if data not found
            } else {
                toast.error(err?.message || 'Something went wrong');
                console.log('response', err);
            }
        } finally {
            setIsBusy(false);
        }
    };

    useEffect(() => {
        fetch();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('Submitting:', data);
        try {
            setIsBusy(true);
            let response;
            if (responseData) {
                response = await fetchWrapper.put(
                    `evv-datablock/client/${clientId}/${BLOCK_IDENTIFIER}?team=${teamId}`,
                    { data }
                );
            } else {
                response = await fetchWrapper.post(
                    `evv-datablock/client/${clientId}/${BLOCK_IDENTIFIER}?team=${teamId}`,
                    { data }
                );
            }
            if (!response?.data) {
                return toast.error('Failed to save data. Please try again later.');
            }
            setData(response?.data || {});
            toast.success('Data saved successfully');
        } catch (e) {
            toast.error(e?.message || 'Something went wrong');
        } finally {
            setIsBusy(false);
        }
    };

    if (isBusy) {
        return (
            <LoadingMessage>
                <VscLoading className="spin" />
            </LoadingMessage>
        );
    }

    if (error) {
        return <Alert variant="danger">Failed to load data</Alert>;
    }

    return (
        <FormContainer>
            <FormTitle>Basic Information</FormTitle>
            <form onSubmit={handleSubmit}>
                <FormGroup>
                    <Label htmlFor="dob">Date of Birth</Label>
                    <Input
                        type="date"
                        id="dob"
                        name="dob"
                        value={data.dob}
                        onChange={handleChange}
                        max={new Date().toISOString().split('T')[0]}
                    />
                </FormGroup>

                <FormGroup>
                    <Label htmlFor="gender">Gender</Label>
                    <Select id="gender" name="gender" value={data.gender} onChange={handleChange}>
                        <option value="">Select gender</option>
                        <option value="M">Male</option>
                        <option value="F">Female</option>
                    </Select>
                </FormGroup>
                <FormGroup>
                    <Label htmlFor="diagnosisCode">Diagnosis Code</Label>
                    <Input
                        type="text"
                        id="diagnosisCode"
                        name="diagnosisCode"
                        value={data.diagnosisCode}
                        onChange={handleChange}
                        placeholder="e.g. F84.0"
                    />
                </FormGroup>

                <Button type="submit">Save Changes</Button>
            </form>
        </FormContainer>
    );
};

export default BasicInformation;
